import { useCallback, useEffect, useRef } from 'react';
import { parseISO, format, startOfMonth, startOfWeek, startOfQuarter, startOfYear, } from 'date-fns';
import { getCategoryExpenses, getCategoryTotals, getMerchantExpenses, } from 'actions';
import { selectHasSetPaydayRange } from 'reducers/selectors';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isTimeDifference15mins } from '../utils/utils';
export const mapStepToDateFnsUnit = (step) => {
    if (!step || step === 'payperiod') {
        return 'months';
    }
    switch (step) {
        case 'isoWeek':
            return 'weeks';
        case 'custom':
            return 'days';
        case 'quarter':
            return 'quarters';
        case 'year':
            return 'years';
        case 'month':
        default:
            return 'months';
    }
};
export const mapStepToStartOf = (step) => {
    if (step === 'isoWeek')
        return startOfWeek;
    if (step === 'quarter')
        return startOfQuarter;
    if (step === 'year')
        return startOfYear;
    return startOfMonth;
};
export const useOpenCategory = (monthlyTotals, position, categoryRoute, step, accountIds, navigationType = 'navigate') => {
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    // TODO come up with a better way to optimize rerenders
    const monthlyTotalsRef = useRef(monthlyTotals);
    monthlyTotalsRef.current = monthlyTotals;
    const positionRef = useRef(position);
    positionRef.current = position;
    const stepRef = useRef(step);
    stepRef.current = step;
    const accountIdsRef = useRef(accountIds);
    accountIdsRef.current = accountIds;
    const isRunning = useRef(false);
    const timeoutRef = useRef(undefined);
    useEffect(() => () => clearTimeout(timeoutRef.current), []);
    return useCallback((data) => {
        const monthlyTotals = monthlyTotalsRef.current;
        const { from, to } = monthlyTotals[positionRef.current] || {};
        if (isRunning.current || !from || !to)
            return;
        isRunning.current = true;
        const totalsToDate = monthlyTotals[0]?.to;
        const totalsFromDate = monthlyTotals[monthlyTotals.length - 1]?.from;
        const categoryId = data.type === 'category' ? data.data.id : undefined;
        const merchantId = data.type === 'merchant' ? data.data.id : undefined;
        if (categoryRoute === 'Category' && totalsFromDate && totalsToDate) {
            dispatch(getCategoryTotals({
                dateFrom: format(parseISO(totalsFromDate), 'yyyy-MM-dd'),
                dateTo: format(parseISO(totalsToDate), 'yyyy-MM-dd'),
                categoryId,
                merchantId,
                isPayDay: selectHasSetPaydayRange(store.getState()),
                step: stepRef.current,
                accountIds: accountIdsRef.current,
            }));
        }
        const isUsing15MinPeriod = isTimeDifference15mins(to, from);
        const fromFormatted = format(parseISO(from), 'yyyy-MM-dd');
        const toFormatted = format(parseISO(to), 'yyyy-MM-dd');
        if (merchantId) {
            dispatch(getMerchantExpenses(isUsing15MinPeriod ? from : fromFormatted, isUsing15MinPeriod ? to : toFormatted, merchantId, accountIdsRef.current));
        }
        else if (categoryId) {
            dispatch(getCategoryExpenses(isUsing15MinPeriod ? from : fromFormatted, isUsing15MinPeriod ? to : toFormatted, categoryId, accountIdsRef.current));
        }
        if (categoryRoute === 'Category') {
            const params = {
                ...(merchantId && {
                    merchantId,
                }),
                ...(categoryId && {
                    categoryId,
                }),
                from: format(parseISO(from), 'yyyy-MM-dd'),
                to: format(parseISO(to), 'yyyy-MM-dd'),
                step: stepRef.current,
                accountIds: accountIdsRef.current,
            };
            if (navigationType === 'push') {
                navigation.push(categoryRoute, params);
            }
            else {
                navigation.navigateDeprecated({ name: categoryRoute, params });
            }
        }
        else {
            const monthlyCategoryParams = {
                ...(merchantId && {
                    merchantId,
                }),
                ...(categoryId && {
                    categoryId,
                }),
                from: isUsing15MinPeriod
                    ? from
                    : format(parseISO(from), 'yyyy-MM-dd'),
                to: isUsing15MinPeriod ? to : format(parseISO(to), 'yyyy-MM-dd'),
                step: stepRef.current,
            };
            const params = monthlyCategoryParams;
            if (navigationType === 'push') {
                navigation.push(categoryRoute, params);
            }
            else {
                navigation.navigateDeprecated({ name: categoryRoute, params });
            }
        }
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            isRunning.current = false;
            timeoutRef.current = undefined;
        }, 300);
    }, [categoryRoute]);
};
